import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useCompetition } from '../contexts/CompetitionContext';
import { competitionRoot, fetchCompetitionLeaderboard, adminRecalculateResults } from '../services/competitionApi';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Users, Trophy, Target, Calendar, Gamepad2, Bell, Flag, HelpCircle, RefreshCw } from 'lucide-react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';

const AdminDashboard = () => {
  const { t, language, isRTL } = useLanguage();
  const navigate = useNavigate();
  const { competitionSlug } = useCompetition();
  const gamesBase = () => `${competitionRoot(competitionSlug)}/games`;
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalGames: 0,
    totalBets: 0,
    activeGames: 0
  });
  const [roundBettingStatus, setRoundBettingStatus] = useState({
    usersWithBets: [],
    selectedRound: null,
    totalGames: 0
  });
  const [availableRounds, setAvailableRounds] = useState([]);
  const [selectedRound, setSelectedRound] = useState('');
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchStats();
    fetchAvailableRounds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionSlug]);

  useEffect(() => {
    if (selectedRound) {
      fetchRoundBettingStatus(selectedRound);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRound]);

  const handleRecalculateResults = async () => {
    const ok = window.confirm(
      language === 'en'
        ? 'Recalculate points for all finished games in this competition? Exact scores and the leaderboard will be updated.'
        : 'לחשב מחדש נקודות לכל המשחקים שהסתיימו בתחרות זו? תוצאות מדויקות וטבלת המובילים יעודכנו.'
    );
    if (!ok) return;
    try {
      setRecalculating(true);
      const summary = await adminRecalculateResults(competitionSlug);
      const changed = summary.betsChanged ?? 0;
      const exact = summary.exactScores ?? 0;
      toast.success(
        language === 'en'
          ? `Recalculated ${summary.betsProcessed ?? 0} bets (${changed} updated, ${exact} exact)`
          : `חושבו מחדש ${summary.betsProcessed ?? 0} הימורים (${changed} עודכנו, ${exact} מדויקים)`
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          (language === 'en' ? 'Failed to recalculate points' : 'נכשל בחישוב מחדש של נקודות')
      );
    } finally {
      setRecalculating(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/users/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error(language === 'en' ? 'Failed to load statistics' : 'נכשל בטעינת הסטטיסטיקות');
    }
  };

  const fetchAvailableRounds = async () => {
    try {
      const gamesResponse = await axios.get(gamesBase());
      const allGames = Array.isArray(gamesResponse.data) ? gamesResponse.data : [];

      const rounds = [...new Set(allGames.map((game) => game.round).filter(Boolean))];
      rounds.sort();

      setAvailableRounds(rounds);

      if (rounds.length > 0) {
        setSelectedRound((prev) => prev || rounds[0]);
      } else {
        setSelectedRound('');
        setRoundBettingStatus({
          usersWithBets: [],
          selectedRound: null,
          totalGames: 0
        });
      }
    } catch (error) {
      console.error('Error fetching available rounds:', error);
      toast.error(language === 'en' ? 'Failed to load rounds' : 'נכשל בטעינת הסיבובים');
      setAvailableRounds([]);
      setSelectedRound('');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoundBettingStatus = async (roundName) => {
    try {
      setLoading(true);
      
      // Fetch all games with error handling
      let allGames = [];
      try {
        const gamesResponse = await axios.get(gamesBase());
        allGames = Array.isArray(gamesResponse.data) ? gamesResponse.data : [];
      } catch (gamesError) {
        console.error('Failed to fetch games:', gamesError);
        throw new Error('Failed to fetch games');
      }
      
      // Get games for selected round
      const roundGames = allGames.filter(game => game.round === roundName);
      
      if (roundGames.length === 0) {
        setRoundBettingStatus({
          usersWithBets: [],
          selectedRound: roundName,
          totalGames: 0
        });
        setLoading(false);
        return;
      }
      
      // Fetch all users with error handling
      let allUsers = [];
      try {
        const usersResponse = await axios.get('/users');
        allUsers = Array.isArray(usersResponse.data) ? usersResponse.data : [];
      } catch (usersError) {
        console.error('Failed to fetch users:', usersError);
        // Try alternative endpoint
        try {
          const leaderboardRows = await fetchCompetitionLeaderboard(competitionSlug);
          allUsers = Array.isArray(leaderboardRows)
            ? leaderboardRows.map((r) => ({
                _id: r.userId,
                username: r.username,
                totalPoints: r.totalPoints
              }))
            : [];
        } catch (leaderboardError) {
          console.error('Failed to fetch users from leaderboard:', leaderboardError);
          throw new Error('Failed to fetch users');
        }
      }
      
      // Get round game IDs first
      const roundGameIds = roundGames.map(game => game._id);
      
      // Fetch all bets with error handling and multiple fallback strategies
      let allBets = [];
      try {
        // Strategy 1: Try to get bets for each game individually
        const betPromises = roundGames.map(async (game) => {
          try {
            const response = await axios.get(`/bets/game/${game._id}/users`);
            const betsData = response.data.bets || response.data || [];
            const processedBets = Array.isArray(betsData) ? betsData.map(bet => ({
              ...bet,
              gameId: bet.gameId || bet.game || game._id, // Ensure gameId is set
            })) : [];
            return processedBets;
          } catch (error) {
            return [];
          }
        });
        
        const gameBets = await Promise.all(betPromises);
        allBets = gameBets.flat(); // Flatten array of arrays
        
        // Strategy 2: If no bets found, try to get from user endpoints
        if (allBets.length === 0) {
          const userBetPromises = allUsers.map(async (user) => {
            try {
              const response = await axios.get(`/users/${user._id}/bets`);
              const userBets = Array.isArray(response.data) ? response.data : [];
              // Filter and ensure gameId for bets in current round
              return userBets
                .map(bet => ({
                  ...bet,
                  gameId: bet.gameId || bet.game || bet.gameId?._id || bet.game?._id
                }))
                .filter(bet => {
                  const gameId = bet.gameId;
                  return roundGameIds.includes(gameId);
                });
            } catch (error) {
              return [];
            }
          });
          
          const userBets = await Promise.all(userBetPromises);
          allBets = userBets.flat();
        }
        
      } catch (betsError) {
        console.error('All betting fetch strategies failed:', betsError);
        allBets = [];
      }
      
      // Calculate betting stats for each user
      const usersWithBets = allUsers.map(user => {
        // Count how many games this user bet on in this round
        const userBetsInRound = allBets.filter(bet => {
          // Try multiple ways to get gameId from bet structure
          const gameId = bet.gameId || bet.game || bet.gameId?._id || bet.game?._id;
          const betUserId = bet.userId || bet.user?._id;
          
          const isInRound = roundGameIds.includes(gameId);
          const isUserBet = betUserId === user._id;
          
          return isInRound && isUserBet;
        });
        
        const betsCount = userBetsInRound.length;
        const totalGames = roundGames.length;
        const betsPercentage = totalGames > 0 ? Math.round((betsCount / totalGames) * 100) : 0;
        
        return {
          ...user,
          betsCount,
          totalGames,
          betsPercentage
        };
      });
      
      // Sort users: those with more bets first, then alphabetically
      usersWithBets.sort((a, b) => {
        if (a.betsCount !== b.betsCount) {
          return b.betsCount - a.betsCount; // Higher bet count first
        }
        return (a.fullName || a.username || '').localeCompare(b.fullName || b.username || '');
      });
      
      setRoundBettingStatus({
        usersWithBets,
        selectedRound: roundName,
        totalGames: roundGames.length
      });
      
    } catch (error) {
      console.error('Error fetching round betting status:', error);
      toast.error(language === 'en' ? 'Failed to load betting status' : 'נכשל בטעינת סטטוס ההימורים');
      setRoundBettingStatus({
        usersWithBets: [],
        selectedRound: roundName,
        totalGames: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, iconBg, cardClass }) => (
    <Card className={cardClass}>
      <div className="flex items-center gap-4">
        <div
          className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-md ${iconBg}`}
        >
          <Icon className="h-6 w-6" strokeWidth={2} />
        </div>
        <div className="min-w-0">
          <p className="text-3xl font-bold tabular-nums text-slate-900">{value}</p>
          <p className="text-sm font-semibold text-slate-600">{title}</p>
        </div>
      </div>
    </Card>
  );

  if (loading && availableRounds.length === 0) {
    return (
      <AdminLayout
        title={t('adminDashboard')}
        subtitle={
          language === 'en' ? 'System overview and management' : 'סקירת המערכת וניהול'
        }
        loading
      />
    );
  }

  return (
    <AdminLayout
      title={t('adminDashboard')}
      subtitle={
        language === 'en' ? 'System overview and management' : 'סקירת המערכת וניהול'
      }
      actions={
        <Button
          size="sm"
          variant="secondary"
          className="!border-white/20 !bg-white/10 !text-white hover:!bg-white/20"
          loading={recalculating}
          disabled={recalculating}
          onClick={handleRecalculateResults}
        >
          <RefreshCw className="h-4 w-4" />
          {language === 'en' ? 'Recalculate points' : 'חשב נקודות מחדש'}
        </Button>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={Users}
          title={t('totalUsers')}
          value={stats.totalUsers}
          iconBg="bg-gradient-to-br from-brand-500 to-brand-700"
          cardClass="border-brand-100 bg-gradient-to-br from-slate-50 to-brand-50/40"
        />
        <StatCard
          icon={Gamepad2}
          title={t('totalGames')}
          value={stats.totalGames}
          iconBg="bg-gradient-to-br from-pitch-500 to-pitch-700"
          cardClass="border-pitch-100 bg-gradient-to-br from-slate-50 to-pitch-50/50"
        />
        <StatCard
          icon={Target}
          title={t('totalBets')}
          value={stats.totalBets}
          iconBg="bg-gradient-to-br from-amber-500 to-amber-700"
          cardClass="border-amber-100 bg-gradient-to-br from-slate-50 to-amber-50/40"
        />
        <StatCard
          icon={Trophy}
          title={t('activeGames')}
          value={stats.activeGames}
          iconBg="bg-gradient-to-br from-red-500 to-red-700"
          cardClass="border-red-100 bg-gradient-to-br from-slate-50 to-red-50/40"
        />
      </div>

      <Card className="border-slate-200 bg-slate-50/80">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
              <Users className="h-5 w-5" strokeWidth={2} />
            </span>
            <h2 className="text-lg font-bold text-slate-900">
              {language === 'en' ? 'Round betting status' : 'סטטוס הימורים לפי סיבוב'}
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm font-semibold text-slate-700">
              {language === 'en' ? 'Round' : 'סיבוב'}
            </label>
            <select
              value={selectedRound}
              onChange={(e) => setSelectedRound(e.target.value)}
              className="min-w-[8rem] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            >
              <option value="">
                {language === 'en' ? 'Select round' : 'בחר סיבוב'}
              </option>
              {availableRounds.map((round) => (
                <option key={round} value={round}>
                  {round}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading && selectedRound ? (
          <div className="flex flex-col items-center py-12 text-brand-700">
            <div className="mb-4 h-10 w-10 animate-spin rounded-full border-2 border-brand-200 border-t-brand-600" />
            <p className="text-sm font-medium">
              {language === 'en' ? 'Loading betting status…' : 'טוען סטטוס הימורים…'}
            </p>
          </div>
        ) : selectedRound && roundBettingStatus.selectedRound ? (
          <div className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm">
                <p className="text-2xl font-bold tabular-nums text-brand-600">
                  {roundBettingStatus.totalGames}
                </p>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {language === 'en' ? 'Games in round' : 'משחקים בסיבוב'}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm">
                <p className="text-2xl font-bold tabular-nums text-pitch-600">
                  {
                    roundBettingStatus.usersWithBets.filter((u) => u.betsCount === u.totalGames)
                      .length
                  }
                </p>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {language === 'en' ? 'Fully picked' : 'הימרו על הכל'}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm">
                <p className="text-2xl font-bold tabular-nums text-amber-600">
                  {roundBettingStatus.usersWithBets.filter((u) => u.betsCount === 0).length}
                </p>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {language === 'en' ? 'No picks' : 'לא הימרו'}
                </p>
              </div>
            </div>

            <div>
              <h3 className="mb-3 text-sm font-semibold text-slate-800">
                {language === 'en' ? 'Users' : 'משתמשים'}
              </h3>
              <ul className="space-y-2">
                {roundBettingStatus.usersWithBets.map((u) => {
                  let badgeClass;
                  if (u.betsCount === 0) {
                    badgeClass = 'bg-red-50 text-red-700 ring-red-100';
                  } else if (u.betsCount === u.totalGames) {
                    badgeClass = 'bg-emerald-50 text-emerald-800 ring-emerald-100';
                  } else {
                    badgeClass = 'bg-amber-50 text-amber-800 ring-amber-100';
                  }
                  const countColor =
                    u.betsCount === 0
                      ? 'text-red-600'
                      : u.betsCount === u.totalGames
                        ? 'text-emerald-600'
                        : 'text-amber-600';
                  return (
                    <li
                      key={u._id}
                      className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-sm font-bold text-white">
                          {(u.fullName || u.username || 'U')[0].toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-slate-900">
                            {u.fullName || u.username}
                          </p>
                          <p className="text-xs text-slate-500">
                            {language === 'en' ? 'ID' : 'מזהה'} …{u._id.slice(-6)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 sm:shrink-0">
                        <div className={isRTL ? 'text-left' : 'text-right'}>
                          <p className={`text-lg font-bold tabular-nums ${countColor}`}>
                            {u.betsCount} {language === 'en' ? '/' : '/'} {u.totalGames}
                          </p>
                          <p className="text-xs text-slate-500">
                            {u.betsPercentage}% {language === 'en' ? 'done' : 'הושלם'}
                          </p>
                        </div>
                        <span
                          className={`min-w-[5.5rem] rounded-lg px-3 py-1 text-center text-xs font-bold ring-1 ${badgeClass}`}
                        >
                          {u.betsCount === 0
                            ? language === 'en'
                              ? 'None'
                              : 'ללא'
                            : u.betsCount === u.totalGames
                              ? language === 'en'
                                ? 'Full'
                                : 'מלא'
                              : language === 'en'
                                ? 'Partial'
                                : 'חלקי'}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center py-12 text-slate-400">
            <Calendar className="mb-3 h-12 w-12 opacity-50" />
            <p className="text-center text-sm">
              {availableRounds.length === 0
                ? language === 'en'
                  ? 'No rounds available'
                  : 'אין סיבובים זמינים'
                : language === 'en'
                  ? 'Select a round to view betting status'
                  : 'אנא בחר סיבוב לצפייה בסטטוס ההימורים'}
            </p>
          </div>
        )}
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-6">
        <Button
          variant="secondary"
          className="h-auto flex-col gap-2 border-2 border-brand-200 py-5 shadow-md"
          onClick={() => navigate('/admin/competitions')}
        >
          <Flag className="h-8 w-8 text-brand-600" strokeWidth={2} />
          {t('manageCompetitions')}
        </Button>
        <Button
          variant="primary"
          className="h-auto flex-col gap-2 py-5 shadow-md"
          onClick={() => navigate('/admin/users')}
        >
          <Users className="h-8 w-8" strokeWidth={2} />
          {t('manageUsers')}
        </Button>
        <Button
          variant="success"
          className="h-auto flex-col gap-2 py-5 shadow-md"
          onClick={() => navigate('/admin/games')}
        >
          <Gamepad2 className="h-8 w-8" strokeWidth={2} />
          {t('manageGames')}
        </Button>
        <Button
          className="h-auto flex-col gap-2 bg-amber-500 py-5 text-white shadow-md hover:bg-amber-600"
          onClick={() => navigate('/admin/teams')}
        >
          <Trophy className="h-8 w-8" strokeWidth={2} />
          {t('manageTeams')}
        </Button>
        <Button
          className="h-auto flex-col gap-2 bg-pink-600 py-5 text-white shadow-md hover:bg-pink-700"
          onClick={() => navigate('/admin/notifications')}
        >
          <Bell className="h-8 w-8" strokeWidth={2} />
          {language === 'en' ? 'Notifications' : 'התראות'}
        </Button>
        <Button
          className="h-auto flex-col gap-2 bg-brand-600 py-5 text-white shadow-md hover:bg-brand-700"
          onClick={() => navigate('/admin/bets')}
        >
          <Target className="h-8 w-8" strokeWidth={2} />
          {language === 'en' ? 'Manage bets' : 'נהל הימורים'}
        </Button>
        <Button
          className="h-auto flex-col gap-2 bg-violet-600 py-5 text-white shadow-md hover:bg-violet-700"
          onClick={() => navigate('/admin/questions')}
        >
          <HelpCircle className="h-8 w-8" strokeWidth={2} />
          {language === 'en' ? 'Predictions Q' : 'שאלות תחזית'}
        </Button>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard; 